import React, { useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { TextButton } from '../../widgets/buttons';
import { FormControl } from '../../widgets/formControl';
import { NotificationBanner } from '../../widgets/notificationBanner';
import { Button1 } from './myAccountComponents';

export const OrangeLabel = styled.label`
    color: #dc6843;
`;

export const BlackLabelEl = styled.label`
    color: black;
    text-decoration: none;
`;

export const SalesChannelsLinkList = styled.div`
    display: inline-block;
    gap: 10px;
`;

export const SalesChannelsLinkListContainer = styled.div`
    margin-left: 4px;
    display: flex;
    gap: 6px;
`;

export const SalesChannelLabel = styled.div`
    width: 100%;
    svg {
        width: 17px;
        height: 17px;
        margin-right: 4px;
    }
    display: flex;
`;

export const Interlinea = styled.span`
    line-height: 2;
`;

export const CancelSubscriptionLabel = styled.div`
    display: flex;
    label {
        align-self: center;
    }
`;

export const BadgeStatusLabel = styled.span<{ $active?: boolean }>`
    display: inline-flex;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    width: fit-content;
    margin-bottom: 16px;
    background: ${(p) => (p.$active ? '#d6f3e2' : '#f0f0f0')};
    color: ${(p) => (p.$active ? '#065f46' : '#6b7280')};
`;

export const BorderedRectangle = styled.div`
    background-color: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.backgroundColor};
    border: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.border};
    border-radius: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.borderRadius};
    padding: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.padding};
    height: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.height};
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
`;

export const ServiceCreditsAddonWrapper = styled(BorderedRectangle)<{ $noCredits?: boolean }>`
    overflow: hidden;
    border: 1px solid transparent;
    background:
        linear-gradient(#fff, #fff) padding-box,
        linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(168, 85, 247, 0.15) 50%, rgba(236, 72, 153, 0.15) 100%) border-box;

    ${(p) =>
        p.$noCredits &&
        `
		animation: credits-card-blink 0.8s ease-in-out infinite;
		@keyframes credits-card-blink {
			0%, 100% {
				box-shadow: none;
			}
			50% {
				box-shadow: 0 3px 16px rgba(124, 58, 237, 0.3), 0 8px 36px rgba(168, 85, 247, 0.2);
			}
		}
	`}
`;

export const PeriodTooltip = styled.div`
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    white-space: nowrap;
`;

export const BorderedRectangleForViewsReport = styled(BorderedRectangle)<{ isFromCurrentBilling: boolean }>`
    padding: 20px;
`;

export const SubscriptionGrid = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
`;

export const SquaresContainer = styled.div`
    --card-columns: 4;
    display: grid;
    grid-template-columns: repeat(var(--card-columns), 1fr);
    gap: 20px;

    @media (max-width: 1440px) {
        --card-columns: 3;
    }

    @media (max-width: 1024px) {
        --card-columns: 2;
    }
`;

export const ReportContainer = styled.div`
    --card-columns: 4;
    display: flex;
    flex-direction: column;
    gap: 20px;

    @media (max-width: 1440px) {
        --card-columns: 3;
    }

    @media (max-width: 1024px) {
        --card-columns: 2;
    }
`;

export const ActivateRemoveContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-top: 16px;
    margin-top: auto;
`;

export const AddOnHeader = styled.div`
    margin-bottom: 4px;

    label {
        color: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockTitle.color};
        font-size: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockTitle.fontSize};
        font-weight: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockTitle.fontWeight};
        font-family: 'Inter', sans-serif;
    }
`;

export const AddPriceSection = styled.div`
    margin-top: auto;
    padding-top: 8px;
    font-size: 13px;
    color: #445a64;

    strong {
        color: #263238;
    }
`;

export const NoSubscriptionWarningBlock = styled.div`
    padding: 20px;
    display: grid;
    gap: 20px;
    justify-content: center;
`;

export const TextButtonMoreAddOns = styled(TextButton)`
    cursor: pointer;
    padding-top: 30px !important;
`;

export const AddOnDescription = styled.p`
    flex: 1;
    font-size: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockDescription.fontSize};
    line-height: 16px;
    color: #546e7a;

    a {
        display: inline-block;
    }
`;

export const AddOnButtonContainer = styled.div`
    justify-content: center;
`;

const BannerContainer = styled(FormControl)`
    margin-bottom: 24px !important;
`;

interface SubscriptionProps {
    subscription: {
        ecommerceName: string;
        subscription: any;
    };
}

const BannerWarningSubscription: React.FC<SubscriptionProps> = ({ subscription }) => {
    const [isWarningVisible, setWarningVisible] = useState(false);
    const [isButtonVisible, setButtonVisible] = useState(true);

    const handleOpenWarning = () => {
        setWarningVisible(true);
        setButtonVisible(false);
    };

    const handleCloseWarning = () => {
        setWarningVisible(false);
        setButtonVisible(true);
    };

    return (
        <div>
            {isButtonVisible && (
                <Button1>
                    <TextButton type='destructive' onClick={handleOpenWarning}>
                        {subscription?.subscription?.inTrial ? T._('Cancel trial', 'Admin') : T._('Cancel subscription', 'Admin')}
                    </TextButton>
                </Button1>
            )}

            {isWarningVisible && (
                <BannerContainer>
                    <NotificationBanner
                        type='WARNING'
                        content={
                            <span>
                                {T._(
                                    `If you want to cancel your subscription you need to remove Zakeke from the apps in your ${subscription.ecommerceName.toLowerCase()} admin panel.`,
                                    'Admin'
                                )}
                            </span>
                        }
                        showCloseButton={true}
                        onClose={handleCloseWarning}
                    />
                </BannerContainer>
            )}
        </div>
    );
};

export default BannerWarningSubscription;
